import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const FRAME = 1 / 30; // ~33ms at 30fps

// Register a "restart from beginning" button once, globally. Sits in the
// control bar in the slot where the volume panel used to be.
const Button = videojs.getComponent('Button');
if (!videojs.getComponent('RestartButton')) {
  class RestartButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('Restart from beginning');
      // Replay circle-arrow icon (inline SVG; matches violet accent on hover via CSS)
      this.el().innerHTML = `
        <span class="vjs-icon-placeholder" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <polyline points="1 4 1 10 7 10"></polyline>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
          </svg>
        </span>`;
    }
    handleClick() {
      const p = this.player();
      p.currentTime(0);
      p.play();
    }
  }
  videojs.registerComponent('RestartButton', RestartButton);
}

export default function VideoPlayer({ src, poster, comments = [], onTimeUpdate, seekRef, onCommentDotClick, onPlayerReady }) {
  const containerRef = useRef(null);
  const playerRef    = useRef(null);
  const [duration,       setDuration]       = useState(0);
  const [progressHolder, setProgressHolder] = useState(null);

  useEffect(() => {
    if (!containerRef.current || !src) return;

    const videoEl = containerRef.current.querySelector('video');
    const player  = videojs(videoEl, {
      controls:       true,
      fluid:          false,
      fill:           true,
      playbackRates:  [0.5, 1, 1.25, 1.5, 2],
      sources:        [{ src, type: detectMimeType(src) }],
      html5:          { vhs: { overrideNative: true } },
      preload:           'auto',
      userActions:       { hotkeys: false },
      // Keep the control bar (play, scrubber, time, volume, etc.) visible at
      // all times — default Video.js fades it out after a few seconds of
      // mouse inactivity during playback.
      inactivityTimeout: 0,
      // Explicit children order — replay sits where volume used to be (right
      // after the play button); volume moves to the far right, next to
      // fullscreen. Custom components must be registered before this runs.
      controlBar: {
        children: [
          'playToggle',
          'restartButton',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'progressControl',
          'remainingTimeDisplay',
          'playbackRateMenuButton',
          'volumePanel',
          'fullscreenToggle',
        ],
      },
      ...(poster ? { poster } : {}),
    });

    playerRef.current = player;

    if (seekRef) {
      seekRef.current = secs => player.currentTime(secs);
    }

    player.ready(() => {
      const holder = player.el().querySelector('.vjs-progress-holder');
      if (holder) {
        holder.style.position = 'relative';
        setProgressHolder(holder);
      }
      onPlayerReady?.();
    });

    player.on('timeupdate', () => onTimeUpdate?.(player.currentTime()));
    player.on('loadedmetadata', () => setDuration(player.duration()));
    // Force first frame to paint so the player isn't black before play
    player.one('loadeddata', () => { if (player.paused()) player.currentTime(0); });

    // Keyboard shortcuts — VJS hotkeys disabled so we handle them here
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const p = playerRef.current;
      if (!p || p.isDisposed()) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        p.currentTime(Math.max(0, p.currentTime() - FRAME));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        p.currentTime(Math.min(p.duration() || 0, p.currentTime() + FRAME));
      } else if (e.key === ' ') {
        e.preventDefault();
        p.paused() ? p.play() : p.pause();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true); // capture phase beats VJS

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      setProgressHolder(null);
      if (seekRef) seekRef.current = null;
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ width: '100%', height: '100%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
      <style>{VJS_OVERRIDES}</style>
      <div ref={containerRef} data-vjs-player style={{ width: '100%', height: '100%' }}>
        <video className="video-js vjs-big-play-centered" playsInline />
      </div>
      {progressHolder && duration > 0 && createPortal(
        comments.filter(c => !c.parentId).map(c => (
          <CommentDot
            key={c.id}
            left={`${(Number(c.timestampSeconds) / duration) * 100}%`}
            onClick={() => {
              playerRef.current?.currentTime(Number(c.timestampSeconds));
              onCommentDotClick?.(c.id);
            }}
          />
        )),
        progressHolder,
      )}
    </div>
  );
}

function CommentDot({ left, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        position:     'absolute',
        left,
        top:          '50%',
        transform:    'translate(-50%, -50%)',
        width:        10,
        height:       10,
        borderRadius: '50%',
        background:   '#22c55e',
        cursor:       'pointer',
        zIndex:       10,
        boxShadow:    '0 0 0 2px rgba(34,197,94,0.45)',
        pointerEvents: 'all',
      }}
    />
  );
}

function detectMimeType(url) {
  if (/\.webm$/i.test(url)) return 'video/webm';
  if (/\.mov$/i.test(url)) return 'video/quicktime';
  return 'video/mp4';
}

const VJS_OVERRIDES = `
  .video-js { background: #07070c; width: 100% !important; height: 100% !important; }
  /* Force the control bar to stay visible at all times — Video.js otherwise
     fades it out during playback (and via the .vjs-user-inactive class even
     when inactivityTimeout is 0). */
  .video-js .vjs-control-bar,
  .video-js.vjs-user-inactive.vjs-playing .vjs-control-bar,
  .video-js.vjs-has-started .vjs-control-bar {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    transition: none !important;
  }
  .vjs-control-bar { background: rgba(7,7,12,0.88) !important; }
  .vjs-play-progress, .vjs-slider-bar { background: var(--violet, #8b6dff) !important; }
  .vjs-volume-level { background: var(--violet, #8b6dff) !important; }
  .vjs-load-progress { background: rgba(139,109,255,0.22) !important; }
  .vjs-big-play-button {
    border-color: var(--violet, #8b6dff) !important;
    background: rgba(7,7,12,0.6) !important;
  }
  .vjs-big-play-button:hover { background: rgba(139,109,255,0.25) !important; }
  .vjs-menu-content { background: #111118 !important; }
  .vjs-menu-item:hover { background: rgba(139,109,255,0.15) !important; }
  .vjs-selected, .vjs-menu-item.vjs-selected { color: var(--violet, #8b6dff) !important; }
`;
